import React from 'react';
import { motion } from 'framer-motion';
import TeamSection from '../components/TeamSection';
import ContactSection from '../components/ContactSection';
import CommunityGallery from '../components/CommunityGallery';

const AboutPage = () => {
  return (
    <>
      <section className="pt-32 pb-16 bg-blue-800 text-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto text-center"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-6">About Snow's Tax & Financial</h1>
            <p className="text-xl">Established in 2008, we are a family-owned business dedicated to helping you achieve financial success.</p>
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center mb-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl font-bold mb-6">Our Story</h2>
              <p className="text-gray-700 mb-6">
                Snow's Tax & Financial was founded with a simple mission: to provide personalized, comprehensive financial guidance that puts clients first. What started as a small advisory practice has grown into a trusted wealth management firm serving clients throughout the region.
              </p>
              <p className="text-gray-700">
                Our growth has been built on referrals from satisfied clients who trust us with their most important financial decisions. We believe that true wealth management goes beyond investment returns – it's about understanding your unique story and helping you write the next chapter.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <img
                src="https://cdn.prod.website-files.com/64d13f6ce094cf9b34b8f52f/64d2a316b9e9dfbd1cf2e6f1_Simeon%20Snow.jpg"
                alt="Simeon Snow"
                className="rounded-lg shadow-lg w-full"
              />
            </motion.div>
          </div>

          {/* Bio Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto mb-16"
          >
            <h2 className="text-3xl font-bold mb-6">Bio</h2>
            <div className="text-gray-700 leading-relaxed space-y-4">
              <p>
                Simeon Snow is a seasoned financial professional, entrepreneur, and civic leader with a strong track record in tax services, wealth management, and public service.
              </p>
              <p>
                He founded Snow's Tax & Financial in 2008, laying the foundation for a career defined by integrity, innovation, and client-focused solutions. He has a Bachelor's Degree in Corporate Finance, and holds Series 7 and 63 licenses, demonstrating his commitment to professional excellence.
              </p>
              <p>
                From 2012 to 2018, Simeon was affiliated with H.D. Vest, which transitioned into Avantax Wealth Management following its acquisition by TaxAct. He continued his advisory work with Bagby Wealth Management (2018–2020), and later joined Intervest International Equities Corp., remaining through its 2024 merger with Ausdal Financial Partners, Inc., where he is currently affiliated.
              </p>
              <p>
                Beyond finance, Simeon has been deeply engaged in community leadership. He was elected to the Clay County Electric Cooperative Board of Directors in 2018 and later served on the Clay County Connect Board, helping expand broadband access across the region. He has also served two terms as Clay County Election Commissioner Chairman, where he continues to uphold electoral integrity.
              </p>
              <p>
                In 2022, Simeon ran as a candidate for Arkansas State Auditor, reflecting his dedication to transparency and public accountability.
              </p>
              <p>He is the founder of Tax Owlchemy Labs, a cutting-edge tax software company and IRS-approved CPE provider. Through this platform, Simeon empowers fellow tax professionals to launch their own practices, navigate credentialing, and build compliant, client-ready systems.</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Community Gallery Section */}
      <CommunityGallery />

      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto text-center mb-16"
          >
            <h2 className="text-3xl font-bold mb-6">Our Approach</h2>
            <p className="text-lg text-gray-700">
              We understand that no two clients are the same. The goal, as experienced & knowledgeable tax & financial professionals, is to design strategies developed specifically for your own unique situation. Those strategies will help you reach your financial goals, while securing the maximum possible tax benefit.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                step: '01',
                title: 'Listen & Understand',
                description: 'We take the time to truly understand your financial situation, goals, and concerns.'
              },
              {
                step: '02',
                title: 'Create a Plan',
                description: 'We develop a customized financial strategy designed specifically for your unique needs.'
              },
              {
                step: '03',
                title: 'Implement & Monitor',
                description: 'We implement your plan and continuously monitor and adjust as your life evolves.'
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                className="bg-white p-8 rounded-xl shadow-lg"
              >
                <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center text-xl font-bold mb-6">
                  {item.step}
                </div>
                <h3 className="text-xl font-bold mb-4">{item.title}</h3>
                <p className="text-gray-700">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <TeamSection />
      <ContactSection />
    </>
  );
};

export default AboutPage;